import React from "react";
import "../css/private-portfolio.css";

const PrivatePortfolio = () => {
  return (
    <main className="private-portfolio">

      {/* =======================
          HERO
      ======================== */}
      <header className="pp-hero">
        <h1 className="pp-name">Steven Hue</h1>

        <p className="pp-clarifier">
          Professional work conducted under legal name: Steve Berry
        </p>

        <h2 className="pp-role">Sound Designer &amp; Audio Producer</h2>

        <p className="pp-meta">
          Melbourne, Australia
        </p>

        <nav className="pp-links">
          <a href="mailto:hue@stevenhue.com">Email</a>
          <a
            href="https://www.linkedin.com/in/steven-hue/"
            target="_blank"
            rel="noreferrer"
          >
            LinkedIn
          </a>

        </nav>
      </header>

      {/* =======================
          ABOUT
      ======================== */}
      <section className="pp-section">
        <h3>About</h3>

        <p>
         I'm a sound designer and audio producer with experience spanning game audio, 
         installation work, and music technology.
        </p>

        <p>
          My work includes sound design and music for game trailers, environment showcases, 
          and game jams, as well as art installations and commissioned audiovisual work.
        </p>

        <p>
          Alongside this, I create audio content for music production software 
          at Scaler Music - designing samples, presets, and musical content while 
          contributing to product testing and development.
        </p>
      </section>

      {/* =======================
          SELECTED SOUND DESIGN & GAME-ADJACENT WORK
      ======================== */}
      <section className="pp-section">
        <h3>Sound Design & Game Audio</h3>

        <article className="pp-item">
          <h4>Returner – Game Environment Showcase</h4>
          <p className="pp-item-meta">
            Sound Designer &amp; Composer · Freelance · 2021
          </p>

          <p>
            Sound design and original music for a series of game environment
            showcase videos created to highlight the visual assets and world-building
            of the <em>Returner</em> game series.
          </p>

          {/* Media placed early for clarity, not emphasis */}
          <div className="pp-media">
            <iframe
              src="https://www.youtube.com/embed/AMvE5snylk0"
              title="Returner – Game Environment Showcase"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          <p>
            I designed environmental sound effects and composed music for three
            distinct sci-fi environments, focusing on atmosphere, spatial depth,
            and tonal cohesion. The work combined cinematic music with detailed
            environmental sound design to support each scene’s visual identity.
          </p>

          <p>
            Techniques included sample layering, synthesis, audio signal processing,
            and final mix and master delivery.
          </p>

          <p className="pp-note">
            <em>
              Note: These videos were produced as environment showcases and are not
              in-engine gameplay audio.
            </em>
          </p>
        </article>

        <article className="pp-item">
          <h4>Torque Drift 2 – Game Trailer</h4>
          <p className="pp-item-meta">
            Sound Designer · s:amplify · 2024
          </p>

     {/* Media placed early for clarity, not emphasis */}
          <div className="pp-media">
            <iframe
              src="https://www.youtube.com/embed/pLkgmr4MBSE"
              title="Torque Drift 2 – Official Update 11.0 Trailer"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          <p>
            Sound design focusing on engine sounds and environmental audio for this racing trailer, 
            completed as part of my role at {" "}
            <a
              href="https://samplify.studio"
              target="_blank"
              rel="noopener noreferrer"
            >
              s:amplify
            </a>.
          </p>

          <p>
            The final edit and mix were developed collaboratively within the team.
          </p>

          {/* <p className="pp-note">
            Audio available upon request.
          </p> */}
        </article>

        <article className="pp-item">
          <h4>Game Audio – Prototypes & Game Jams</h4>
          <p className="pp-item-meta">
            Sound Designer &amp; Composer · Game Jams · 2021
          </p>

          <p>
            Sound design and music for multiple game jam projects, including{" "}
            <em>Gro-Wing</em> and <em>Morph Wars</em>.
          </p>

          <p>
            I composed music with varying intensity in mind to support different
            gameplay moments, and designed sound effects using a combination of
            field recording, layering, and processing to bring interactions to life.
            All work was completed under tight time constraints within small,
            collaborative teams.
          </p>

          <p className="pp-note">
            Project links (itch.io):{" "}
            <a href="https://sergeymakesagame.itch.io/gro-wing"target="_blank" rel="noopener noreferrer">Gro-Wing</a> &amp;{" "}
            <a href="https://dense13.itch.io/morph-wars"target="_blank" rel="noopener noreferrer">Morph Wars</a>
          </p>

          <img
            src="/images/growing.png"
            alt="Gro-Wing game screenshot"
          />
        </article>

        <article className="pp-item">
          <h4>Art Installations &amp; Audiovisual Works</h4>
          <p className="pp-item-meta">
            Sound Designer &amp; Composer · Various Commissions · 2017–2022
          </p>

          <p>
            Sound design and music for large-scale art installations and commissioned
            audiovisual works, created in collaboration with visual artists and
            cultural institutions.
          </p>

          <p>
            Projects included long-form, loopable soundscapes for immersive and
            spatial installations, as well as audiovisual narratives for digital
            presentation. I worked closely with artists to translate conceptual ideas 
            into sound while addressing technical constraints such as
            continuous playback, spatial presentation, and audience interaction.
          </p>

          <p>
            Selected projects include <em>Journey of a Million Miles</em> and{" "}
            <em>Total Internal Reflection</em> (commissioned works by Tiffany Singh),
            and <em>Tashi Dolma</em>, a commissioned audiovisual narrative combining
            original music with edited visual storytelling.
          </p>
        </article>
      </section>

      {/* =======================
          MUSIC TECHNOLOGY & PRODUCT AUDIO
      ======================== */}
      <section className="pp-section">
        <h3>Music Technology & Audio Content</h3>

        <article className="pp-item">
          <h4>Scaler Music – Audio Content &amp; Sound Design</h4>
          <p className="pp-item-meta">
            Sound Designer &amp; Audio Content Creator · Scaler Music · 2024–Present
          </p>

          <p>
            Sound design and musical content created for use within music production software, 
            contributing to both creative assets and product development workflows.
          </p>

          <p>
            For <strong>DC Snares</strong>, I designed production-ready drum samples
            and presets ranging from experimental textures to flexible, mix-ready
            hits. Work included sample creation, layering, processing, and extensive
            testing to ensure consistency, usability, and creative range.
          </p>

          <p>
            I composed original chord sets for <strong>Scaler 3</strong> and
            earlier versions, creating genre-spanning musical content used by
            thousands of producers worldwide. Chord sets were designed with musical
            intent, usability, and adaptability in mind.
          </p>

          <p>
            Alongside audio content creation, I’ve contributed to plugin testing, 
            UI/UX feedback, and technical documentation, and <a href="https://www.youtube.com/watch?v=x3xwMR0GpFE"target="_blank" rel="noopener noreferrer">presented a technical overview video</a> for a DC Snares update on the Scaler Music YouTube channel.
          </p>
        </article>
      </section>

      {/* =======================
          ADDITIONAL EXPERIENCE
      ======================== */}
      <section className="pp-section">
        <h3>Additional Experience</h3>

        <article className="pp-item">
          {/* <h4>Education</h4> */}
          <p className="pp-item-meta">
            Sound Design Instructor · School of Synthesis · 2024 
          </p>

          <p>
           Developed and delivered online sound design lessons for the <a href="https://online.schoolofsynthesis.com/sos-sound-design-online-course"target="_blank" rel="noopener noreferrer">SOS Sound Design Online Course</a>, 
           focusing on practical approaches to sampling, layering audio, effects, and mixing.
          </p>
        </article>

        <article className="pp-item">
          {/* <h4>Video Production &amp; Motion Graphics</h4> */}
          <p className="pp-item-meta">
            Video, Motion Graphics & Visual Design · Various Projects · 2017–Present
          </p>

          <p>
            Produced video, motion graphics, and visual design assets within the music technology 
            industry and for commissioned projects. The focus of this work was translating technical 
            or creative ideas into clear, engaging visual content while maintaining brand consistency.
          </p>

             <p>
            Examples include video editing and motion graphics for Scaler Music marketing and educational content, 
            designing thumbnails for YouTube, and preparing brand assets for web, desktop apps, and iOS. 
            I’ve also delivered short-form video and visual content for freelance and personal projects.
          </p>
        </article>
      </section>

      {/* =======================
          TECHNICAL SKILLS
      ======================== */}
      <section className="pp-section">
        <h3>Technical Proficiencies</h3>

        <ul className="pp-skills">
          <li><strong>Audio:</strong> Logic Pro, Ableton Live, sound design, music composition, synthesis, sampling, mixing and mastering, introductory experience with FMOD</li>
          <li><strong>Video & Design:</strong> Premiere Pro, After Effects, ScreenFlow 10, InDesign, Photoshop, Illustrator </li>
          <li><strong>Workflow & Collaboration:</strong> Google Workspace, Apple iWork apps, Slack, Jira</li>
        </ul>
      </section>

      {/* =======================
          FOOTER
      ======================== */}
      <footer className="pp-footer">
        <p>
          Available for sound design, audio production, and game audio–focused roles.
        </p>

        <nav className="pp-links">
          <a href="mailto:hue@stevenhue.com">Email</a>
          <a
            href="https://www.linkedin.com/in/steven-hue"
            target="_blank"
            rel="noreferrer"
          >
            LinkedIn
          </a>
        </nav>

        <p className="pp-updated">
          Portfolio last updated: 2025
        </p>
      </footer>

    </main>
  );
};

export default PrivatePortfolio;
